import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { api } from "../../lib/api";
import { PROVIDER_REASONING_LEVELS, reasoningLabel } from "../../lib/reasoning";
import { SavedNote } from "../shared/Icons";
import { toastError } from "../shared/toast";

interface InsightsConfig {
  prompt: string;
  provider: string;
  model: string;
  temperature: number;
  reasoning: string;
}

interface AvailableModel {
  id: string;
  provider: string;
  model_name: string;
  label: string;
  thinking_levels: string[];
}

const PROVIDERS = ["openai", "anthropic", "google", "grok", "openrouter", "deepseek", "meta"];
const PROVIDER_LABEL: Record<string, string> = {
  openai: "OpenAI", anthropic: "Anthropic", google: "Google", grok: "Grok", openrouter: "OpenRouter", deepseek: "DeepSeek", meta: "Meta",
};

const inp: React.CSSProperties = {
  padding: "8px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line-strong)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
  width: "100%",
  boxSizing: "border-box",
};

const lbl: React.CSSProperties = { fontSize: 12, color: "var(--ink-soft)", display: "block", marginBottom: 5 };

export function InsightsConfigTab() {
  const qc = useQueryClient();

  const { data: config, isLoading } = useQuery<InsightsConfig>({
    queryKey: ["dev-insights-config"],
    queryFn: () => api.get("/dev/insights-config"),
  });

  const { data: models = [] } = useQuery<AvailableModel[]>({
    queryKey: ["dev-available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });

  const [prompt, setPrompt] = useState("");
  const [provider, setProvider] = useState("google");
  const [model, setModel] = useState("");
  const [temperature, setTemperature] = useState("0.3");
  const [reasoning, setReasoning] = useState("off");
  const [loaded, setLoaded] = useState(false);

  // Hidrata o form quando o GET retorna (uma vez).
  useEffect(() => {
    if (!config || loaded) return;
    setPrompt(config.prompt ?? "");
    setProvider(config.provider ?? "google");
    setModel(config.model ?? "");
    setTemperature(String(config.temperature ?? 0.3));
    setReasoning(config.reasoning ?? "off");
    setLoaded(true);
  }, [config, loaded]);

  const providerModels = models.filter((m) => m.provider === provider);
  const levels = PROVIDER_REASONING_LEVELS[provider] ?? [];
  const reasoningOptions = levels.includes("off") ? levels : ["off", ...levels];

  const mut = useMutation({
    mutationFn: () => {
      // PATCH só com os campos alterados em relação ao GET.
      const body: Partial<InsightsConfig> = {};
      if (config) {
        if (prompt !== config.prompt) body.prompt = prompt;
        if (provider !== config.provider) body.provider = provider;
        if (model !== config.model) body.model = model;
        const temp = parseFloat(temperature.replace(",", "."));
        if (Number.isFinite(temp) && temp !== config.temperature) body.temperature = temp;
        if (reasoning !== config.reasoning) body.reasoning = reasoning;
      }
      return api.patch("/dev/insights-config", body);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["dev-insights-config"] }),
    onError: (e: any) => toastError(e?.payload?.detail ?? "Falha ao salvar a configuração do revisor."),
  });

  function changeProvider(p: string) {
    setProvider(p);
    const first = models.find((m) => m.provider === p);
    setModel(first?.model_name ?? "");
    const lv = PROVIDER_REASONING_LEVELS[p] ?? [];
    if (!lv.includes(reasoning) && reasoning !== "off") setReasoning("off");
  }

  if (isLoading) {
    return <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>Carregando...</div>;
  }

  return (
    <div style={{ maxWidth: 640, display: "flex", flexDirection: "column", gap: 16 }}>
      <div>
        <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)" }}>Revisor diário de conversas</span>
        <div style={{ fontSize: 12, color: "var(--ink-muted)", marginTop: 4 }}>
          Config global do revisor diário de conversas. O toggle por agente fica na aba Insights de cada agente.
        </div>
      </div>

      <div>
        <label style={lbl}>Prompt do revisor</label>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          spellCheck={false}
          style={{ ...inp, height: 320, resize: "vertical", fontFamily: "var(--font-mono)", lineHeight: 1.5 }}
        />
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Provedor</label>
          <select style={inp} value={provider} onChange={(e) => changeProvider(e.target.value)}>
            {PROVIDERS.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p]}</option>)}
          </select>
        </div>
        <div style={{ flex: 2 }}>
          <label style={lbl}>Modelo</label>
          {providerModels.length > 0 ? (
            <select style={inp} value={model} onChange={(e) => setModel(e.target.value)}>
              {!providerModels.some((m) => m.model_name === model) && model && (
                <option value={model}>{model}</option>
              )}
              {providerModels.map((m) => (
                <option key={m.id} value={m.model_name}>{m.label}</option>
              ))}
            </select>
          ) : (
            <input
              style={inp}
              value={model}
              onChange={(e) => setModel(e.target.value)}
              placeholder="ex: gemini-3-pro"
              spellCheck={false}
            />
          )}
        </div>
      </div>

      <div style={{ display: "flex", gap: 12 }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Temperatura (0–2)</label>
          <input
            style={inp}
            type="number"
            min={0}
            max={2}
            step={0.1}
            value={temperature}
            onChange={(e) => setTemperature(e.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>Raciocínio</label>
          <select style={inp} value={reasoning} onChange={(e) => setReasoning(e.target.value)}>
            {reasoningOptions.map((t) => (
              <option key={t} value={t}>{reasoningLabel(t)}</option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <button
          onClick={() => mut.mutate()}
          disabled={mut.isPending || !model.trim()}
          style={{ padding: "8px 20px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer", alignSelf: "flex-start" }}
        >
          {mut.isPending ? "Salvando..." : "Salvar"}
        </button>
        <SavedNote show={mut.isSuccess} />
      </div>
    </div>
  );
}
